import React, { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, LayoutGrid } from 'lucide-react';
import { stripDays } from './DayStrip';

const todayET = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });

/** Spreadsheet-style tabs along the bottom of the schedule: "Monthly View" + a tab per day (+ the first 7 of next month = the sheet's "Day 1–7" tabs). */
export const ScheduleDayTabs = ({ monthKey, onMonth, active, onPick, counts = {} }) => {
  const days = stripDays(monthKey);
  const today = todayET();
  const [y, m] = monthKey.split('-').map(Number);
  const label = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const shift = (n) => { const d = new Date(y, m - 1 + n, 1); onMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };
  const scroller = useRef(null);
  useEffect(() => {
    const el = scroller.current?.querySelector(`[data-testid="sched-tab-${active || today}"]`);
    if (el) el.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [active, monthKey, today]);
  let lastMonth = null;
  return (
    <div className="flex items-stretch border-t border-slate-300 bg-slate-100 flex-shrink-0 select-none" data-testid="schedule-day-tabs">
      <button onClick={() => onPick(null)}
        className={`px-3 text-xs font-bold whitespace-nowrap border-r border-slate-300 inline-flex items-center gap-1.5 ${!active ? 'bg-white text-sky-800 shadow-[inset_0_3px_0_#0369a1]' : 'text-slate-600 hover:bg-slate-200'}`}
        data-testid="sched-tab-month">
        <LayoutGrid className="w-3.5 h-3.5" /> Monthly View
      </button>
      <button onClick={() => shift(-1)} className="px-1.5 text-slate-500 hover:bg-slate-200 border-r border-slate-300" title="Previous month's tabs" data-testid="sched-tabs-prev"><ChevronLeft className="w-4 h-4" /></button>
      <span className="px-2 text-[10px] font-bold uppercase tracking-wide text-slate-500 self-center whitespace-nowrap" data-testid="sched-tabs-month">{label}</span>
      <div ref={scroller} className="flex-1 flex overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
        {days.map(d => {
          const dt = new Date(`${d}T00:00:00`);
          const mon = dt.toLocaleDateString('en-US', { month: 'short' });
          const newMonth = mon !== lastMonth && lastMonth !== null; lastMonth = mon;
          const sel = d === active; const isToday = d === today; const wk = dt.getDay() === 0 || dt.getDay() === 6;
          const n = counts[d];
          return (
            <button key={d} onClick={() => onPick(d)} data-testid={`sched-tab-${d}`}
              title={`${dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${n ? ` · ${n} scheduled` : ''}`}
              className={`relative min-w-[40px] px-1.5 py-1.5 text-center leading-tight border-r border-slate-300 flex flex-col items-center justify-center ${newMonth ? 'border-l-2 border-l-amber-500' : ''} ${sel ? 'bg-white text-sky-900 shadow-[inset_0_3px_0_#0369a1]' : isToday ? 'bg-sky-50 text-slate-900 hover:bg-white' : wk ? 'text-slate-400 hover:bg-slate-200' : 'text-slate-700 hover:bg-slate-200'}`}>
              <span className={`text-[8px] font-bold uppercase ${newMonth || dt.getDate() === 1 ? 'text-amber-700' : 'text-slate-400'}`}>{dt.getDate() === 1 ? mon : dt.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2)}</span>
              <span className={`text-xs font-bold ${isToday && !sel ? 'text-sky-700' : ''}`}>{dt.getDate()}</span>
              {n ? <span className="text-[8px] font-semibold text-slate-500 leading-none">{n}</span> : <span className="text-[8px] leading-none text-transparent">·</span>}
            </button>
          );
        })}
      </div>
      <button onClick={() => shift(1)} className="px-1.5 text-slate-500 hover:bg-slate-200 border-l border-slate-300" title="Next month's tabs" data-testid="sched-tabs-next"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
};
